/**
 * FIND, ON EVERY LIST.
 *
 * Anthony: "put a search bar over the top of every column and page so that
 * it's easy to get to."
 *
 * IT FILTERED WHAT WAS ON SCREEN, AND THAT WAS THE BUG. Anthony: "search
 * bars aren't working well or at all either." A list showing a page of
 * jobs was filtered to that page, not searched across nineteen hundred —
 * so a customer's name found them when they happened to be in the window
 * and found nothing when they were not, and nothing on screen said which
 * had happened. That is worse than no search box, because you conclude the
 * record is gone.
 *
 * SO IT DOES BOTH, AND THE LENGTH OF WHAT YOU TYPED DECIDES WHICH. One or
 * two characters narrows what is drawn, which is instant and is what you
 * want while scanning a list you can see. Three or more hands the term to
 * whoever gave this bar a `deepSearch` and lets them ask the database.
 *
 * A list with no `deepSearch` behaves exactly as before and says so in its
 * counter, because a bar that pretends to search everything on a screen
 * that cannot is the original bug wearing a hat.
 *
 * MATCHES ON EVERYTHING VISIBLE. A shop looks for "Tundra", "German",
 * "1188" and "brakes" in the same box without thinking about which column
 * any of them lives in, so the haystack is the row's own text.
 */

import { esc } from "./dom.ts";

export type FindSpec = {
  /** Where the bar is drawn. */
  hostId: string;
  /** The container whose children get filtered. */
  listId: string;
  /** What one row is called, for the counter. */
  noun: string;
  /** Only these are treated as rows; everything else in the list is left alone. */
  rowSelector?: string;
  /** Said under the box when nothing matches. */
  onEmpty?: string;
  /**
   * Ask the database instead of the page. Given the term, it should
   * redraw the list itself and return how many it found. Absent means
   * this list is small enough that the page in front of you IS the data.
   */
  deepSearch?: (term: string) => Promise<number>;
  /** Put the unfiltered list back when the box is cleared. */
  redraw?: () => void;
};

export function findBarHtml(placeholder = "Find anything on this page"): string {
  return `
    <div class="findbar">
      <span class="findbar-i" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
          stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.6-3.6"/></svg>
      </span>
      <input type="search" class="findbar-q"
             autocomplete="off" spellcheck="false" aria-label="${esc(placeholder)}">
      <span class="findbar-n"></span>
    </div>`;
}

/**
 * Wire it up. Filtering happens in the browser against text already on the
 * page, so there is no round trip and no debounce needed — it keeps up
 * with typing on a phone.
 */
export function wireFindBar(spec: FindSpec): void {
  const host = document.getElementById(spec.hostId);
  const list = document.getElementById(spec.listId);
  if (!host || !list) return;

  const input = host.querySelector<HTMLInputElement>(".findbar-q");
  const count = host.querySelector<HTMLElement>(".findbar-n");
  if (!input) return;

  const rows = (): HTMLElement[] =>
    Array.from(list.querySelectorAll<HTMLElement>(spec.rowSelector ?? ":scope > *"))
      .filter((el) => !el.classList.contains("findbar-empty"));

  /** Three characters is a search; fewer is a filter. */
  const DEEP_AT = 3;
  let deepFor = "";
  let timer: ReturnType<typeof setTimeout> | undefined;

  const apply = (): void => {
    const q = input.value.trim().toLowerCase();
    const all = rows();
    let shown = 0;

    for (const el of all) {
      // Words in any order, all of them present. "german tundra" finds the
      // same row as "tundra german", which is how people actually type.
      const hay = (el.textContent ?? "").toLowerCase();
      const hit = !q || q.split(/\s+/).every((w) => hay.includes(w));
      el.hidden = !hit;
      if (hit) shown++;
    }

    if (count) {
      count.textContent = q
        ? `${shown} of ${all.length}`
        : `${all.length} ${all.length === 1 ? spec.noun : `${spec.noun}s`}`;
    }

    let none = list.querySelector<HTMLElement>(".findbar-empty");
    if (q && shown === 0) {
      if (!none) {
        none = document.createElement("div");
        none.className = "empty findbar-empty";
        list.appendChild(none);
      }
      none.textContent = spec.onEmpty
        ?? `Nothing here matches “${input.value.trim()}”. It may be on another page.`;
      none.hidden = false;
    } else if (none) {
      none.hidden = true;
    }
  };

  /**
   * The database is asked after a pause, and only for what is worth
   * asking about. Every keystroke would be a query per letter, and the
   * answer to "j" is every job in the shop.
   */
  const think = (): void => {
    const q = input.value.trim();
    clearTimeout(timer);

    if (!spec.deepSearch) { apply(); return; }

    if (q.length < DEEP_AT) {
      // Back to the page, and back to filtering it.
      if (deepFor) { deepFor = ""; spec.redraw?.(); }
      else apply();
      return;
    }

    apply();                       // narrow what is drawn straight away
    timer = setTimeout(() => {
      const term = input.value.trim();
      if (term.length < DEEP_AT || term === deepFor) return;
      deepFor = term;
      if (count) count.textContent = "searching…";
      void spec.deepSearch!(term)
        .then((n) => {
          if (input.value.trim() !== term) return;   // they typed on
          if (count) {
            count.textContent = n
              ? `${n} found${n >= 200 ? "+" : ""}`
              : "nothing anywhere";
          }
        })
        .catch(() => { if (count) count.textContent = "search failed"; });
    }, 320);
  };

  input.addEventListener("input", think);
  // Escape clears, because a filter you cannot get out of is a trap.
  input.addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key === "Escape") { input.value = ""; apply(); }
  });
  apply();
}
